import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from '../../../../components';
import { Field, Form } from 'react-final-form';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import {
  createAuthorSubscriptionPrice,
  createSubscriptionPlanSelector,
  retrieveSubscriptionPrice,
} from '../../../../ducks/stripe.duck';
import { userSelector } from '../../../../ducks/user.duck';
import { getUserSubscriptionLookupkeys } from '../../../../util/userDataExtraction';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import css from './EditListingTalentPlusPanel.module.css';
import { createTitleGetter } from '../../../../util/subscriptionHelpers';

const featureOptions = [
  { value: 'videos', label: 'Talent-Published Videos' },
  { value: 'videoCalls', label: '1-on-1 Video Calls' },
  { value: 'liveStreaming', label: 'Live Streaming Q&A Sessions' },
  { value: 'fanChats', label: 'Fan Chats' },
  { value: 'socialMedia', label: 'Integrated Social Media Feeds', noFrequency: true },
  { value: 'unlockableContent', label: 'All Unlockable Content', noFrequency: true },
];

const CreateSubscriptionPriceModal = ({
  isOpen,
  onClose,
  intl,
  values,
  selectedSubscription,
  isTalentPlus,
  isBrandPremier,
}) => {
  const dispatch = useDispatch();
  const { createSubscriptionPriceInProgress } = useSelector(createSubscriptionPlanSelector);
  const currentUser = useSelector(userSelector);

  const selectedProduct = selectedSubscription?.product || {};

  const initialValues = {
    title: selectedProduct?.name || '',
    description: selectedSubscription?.metadata?.description || '',
    subscriptionType: selectedSubscription?.metadata?.subscriptionType || 'Recurring',
    price: selectedSubscription?.unit_amount ? selectedSubscription.unit_amount / 100 : '',
    billingCycle: selectedSubscription?.recurring?.interval || '',
    customFeatures: {},
    tasks: [''],
  };

  const required = value =>
    value
      ? undefined
      : intl.formatMessage({
          id: 'CreateSubscriptionPriceModal.required',
          defaultMessage: 'Required',
        });

  const handleCreateSubscriptionPlan = async formValues => {
    const { title, description, price, billingCycle, customFeatures, tasks } = formValues || {};
    await dispatch(
      createAuthorSubscriptionPrice({
        title,
        description,
        amount: price,
        subscriptionType: 'Recurring',
        billingCycle,
        customFeatures,
        authorId: currentUser?.id?.uuid,
        lookupKeys: getUserSubscriptionLookupkeys(currentUser),
      })
    );

    onClose();
  };

  // Usage
  const titleConditions = { isTalentPlus, isBrandPremier };
  const getTitle = createTitleGetter(titleConditions);

  const modalTitle = getTitle({
    isTalentPlus: intl.formatMessage({
      id: 'CreateSubscriptionPriceModal.title',
      defaultMessage: 'Create Subscription Plan',
    }),
    isBrandPremier: intl.formatMessage({
      id: 'CreateSubscriptionPriceModal.brandTitle',
      defaultMessage: 'Create Subscription Box',
    }),
  });

  const brandTasksTitle = getTitle({
    isTalentPlus: intl.formatMessage({
      id: 'CreateSubscriptionPriceModal.tasksLabel',
      defaultMessage: 'Tasks',
    }),
    isBrandPremier: intl.formatMessage({
      id: 'CreateSubscriptionPriceModal.brandTasksLabel',
      defaultMessage: 'Add Subscription Item',
    }),
  });

  const addItemsButtonTitle = getTitle({
    isTalentPlus: intl.formatMessage({
      id: 'CreateSubscriptionPriceModal.addTask',
      defaultMessage: 'Add Task',
    }),
    isBrandPremier: intl.formatMessage({
      id: 'CreateSubscriptionPriceModal.addBrandItem',
      defaultMessage: 'Add Another Item',
    }),
  });

  return (
    <div className={css.createSubscriptionPriceModal}>
      <Modal
        id="subscriptionPlanModal"
        isOpen={isOpen}
        onClose={onClose}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        modalTitle={modalTitle}
      >
        <Form
          onSubmit={handleCreateSubscriptionPlan}
          initialValues={initialValues}
          mutators={{ ...arrayMutators }}
          render={({ handleSubmit, form, values, invalid }) => (
            <form onSubmit={handleSubmit}>
              <div className={css.formContent}>
                {/* Title Field */}
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.titleLabel',
                        defaultMessage: 'Title',
                      })}
                    </label>
                    <Field
                      name="title"
                      component="input"
                      type="text"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.titlePlaceholder',
                        defaultMessage: 'Subscription Name',
                      })}
                      validate={required}
                    />
                  </div>
                </div>

                {/* Description Field */}
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.descriptionLabel',
                        defaultMessage: 'Description',
                      })}
                    </label>
                    <Field
                      name="description"
                      component="textarea"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.descriptionPlaceholder',
                        defaultMessage: 'Details about the subscription',
                      })}
                      validate={required}
                    />
                  </div>
                </div>

                {/* Price and Billing Cycle Fields */}
                <div className={classNames(css.formRow)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.priceLabel',
                        defaultMessage: 'Price in USD',
                      })}
                    </label>
                    <Field
                      name="price"
                      component="input"
                      type="number"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.pricePlaceholder',
                        defaultMessage: 'Enter price',
                      })}
                      validate={required}
                    />
                  </div>

                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.billingCycleLabel',
                        defaultMessage: 'Billing Cycle',
                      })}
                    </label>
                    <Field name="billingCycle" component="select" validate={required}>
                      <option value="">
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.selectCycle',
                          defaultMessage: 'Select cycle',
                        })}
                      </option>
                      <option value="month">
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.monthlyOption',
                          defaultMessage: 'Monthly',
                        })}
                      </option>
                      <option value="year">
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.yearlyOption',
                          defaultMessage: 'Yearly',
                        })}
                      </option>
                    </Field>
                  </div>
                </div>

                {/* Features */}
                {isTalentPlus && (
                  <div className={classNames(css.formRow, css.fullWidth)}>
                    <div className={css.formFld}>
                      <label>
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.featuresLabel',
                          defaultMessage: 'Select Features',
                        })}
                      </label>
                      <div className={css.featureOptionSec}>
                        <FieldArray name="customFeatures">
                          {({ fields }) => (
                            <>
                              {featureOptions.map(feature => (
                                <div key={feature.value} className={css.featureOption}>
                                  <div className={css.featureLabel}>
                                    <Field
                                      name={`customFeatures.${feature.value}.enabled`}
                                      component="input"
                                      type="checkbox"
                                    />
                                    {feature.label}
                                  </div>
                                  {!feature.noFrequency && fields.value?.[feature.value]?.enabled && (
                                    <Field
                                      name={`customFeatures.${feature.value}.frequency`}
                                      component="input"
                                      type="number"
                                      placeholder={intl.formatMessage({
                                        id: 'CreateSubscriptionPriceModal.frequencyPlaceholder',
                                        defaultMessage: 'Quantity Provided Per Billing Cycle',
                                      })}
                                      validate={required}
                                      className={css.frequencyField}
                                    />
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tasks Field Array */}
                {!isTalentPlus && (
                  <div className={classNames(css.formRow, css.fullWidth)}>
                    <div className={css.formFld}>
                      <label>{brandTasksTitle}</label>
                      <FieldArray name="tasks">
                        {({ fields }) => (
                          <div className={css.tasksContainer}>
                            {fields.map((name, index) => (
                              <div key={name} className={css.taskItem}>
                                <Field
                                  name={name}
                                  component="input"
                                  type="text"
                                  placeholder={intl.formatMessage({
                                    id: isTalentPlus
                                      ? 'CreateSubscriptionPriceModal.taskPlaceholder'
                                      : 'CreateSubscriptionPriceModal.brandTaskPlaceholder',
                                    defaultMessage: 'Enter task',
                                  })}
                                  validate={required}
                                  className={css.inputField}
                                />
                                <button
                                  type="button"
                                  onClick={() => fields.remove(index)}
                                  className={css.removeButton}
                                >
                                  {intl.formatMessage({
                                    id: 'CreateSubscriptionPriceModal.removeTask',
                                    defaultMessage: 'Remove',
                                  })}
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => fields.push('')}
                              className={css.addButton}
                            >
                              {addItemsButtonTitle}
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className={css.buttonWrapper}>
                  <Button
                    type="submit"
                    inProgress={createSubscriptionPriceInProgress}
                    className={css.submitButton}
                    disabled={invalid}
                  >
                    {intl.formatMessage({
                      id: 'CreateSubscriptionPriceModal.submitButton',
                      defaultMessage: 'Save',
                    })}
                  </Button>
                </div>
              </div>
            </form>
          )}
        />
      </Modal>
    </div>
  );
};

export default CreateSubscriptionPriceModal;
